import React from "react";
import { Card, Icon, List, ListItem, VStack } from "@astryxdesign/core";
import type { IconType } from "@astryxdesign/core";
import SectionHeader from "./SectionHeader";
import styles from "./ListCard.module.css";

/** A single row. `onClick` and `href` are mutually exclusive. */
export interface ListCardItem {
  /** Icon rendered before the label. */
  icon?: IconType;
  label: string;
  /** Content after the label — typically a chevron or a Badge. */
  endContent?: React.ReactNode;
  /** Handler for a row that acts in place, e.g. opening a dialog. */
  onClick?: () => void;
  /** Destination for a row that navigates. */
  href?: string;
}

export interface ListCardProps {
  /** Section heading above the rows. */
  title: string;
  items: ListCardItem[];
}

/**
 * A card holding a titled list of rows, sized to fill its column: the rows
 * spread through the height the card is given, so it ends level with taller
 * content beside it (see `AsideRow`).
 *
 * Renders via Astryx's Card/List, so this MUST be rendered inside an Astryx
 * `<Theme>` scope.
 */
const ListCard = ({ title, items }: ListCardProps): React.ReactElement => {
  return (
    <Card variant="default" className={styles.card}>
      <VStack gap={4} className={styles.body}>
        <SectionHeader title={title} />
        <List className={styles.list}>
          {items.map((item) => (
            <ListItem
              key={item.label}
              label={item.label}
              startContent={item.icon ? <Icon icon={item.icon} /> : undefined}
              endContent={item.endContent}
              onClick={item.onClick}
              href={item.href}
            />
          ))}
        </List>
      </VStack>
    </Card>
  );
};

export default ListCard;
